import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import { withTranslation } from 'gatsby-plugin-react-i18next'
import StoreContext from 'src/context/StoreContext'

import Analytics from 'src/utils/Analytics'
import SEO from 'src/components/seo'
import { GatsbyImage } from 'gatsby-plugin-image'
import { DefaultFrameConfig } from 'src/utils/FrameProductPage'
import ReviewsSection from '../../components/ReviewsSection'
import { Anchor } from '../../components/ReviewsSection/styles'
import {
  LOCALIZATION,
  IS_FOREIGN_COUNTRY,
  LOCALIZATION_MAP,
  IN_SEASONAL_STATE,
  VIP_CODE,
} from '../../utils/constants'
import FrameProductForm from '../../components/FrameProductForm'
import {
  ShopifyDescription,
  Thumbnail,
  ThumbnailRow,
  ThumbnailButton,
  Section,
  Container,
  ProductGridLeft,
  ProductGridRight,
  Grid,
  ProductImage,
  GiftModeImage,
  LeftArrow,
  RightArrow,
  ArrowContainer,
} from './styles'

class FrameProductPage extends React.Component {
  constructor(props) {
    super(props)
    const { data } = props
    const { product } = data
    const defaultConfig = DefaultFrameConfig(product.handle, {
      color: 'Black',
      size: '10',
      style: 'Classic',
    })
    this.state = {
      shownImageIndex: 0,
      size: defaultConfig.size,
      style: defaultConfig.style,
      isPlusProduct: true,
      color: defaultConfig.color,
      isScrollable: false,
    }
    this.setShownImageIndex = this.setShownImageIndex.bind(this)
    this.analyticsTitle = this.analyticsTitle.bind(this)
    this.handleSizeSelection = this.handleSizeSelection.bind(this)
    this.handleStyleSelection = this.handleStyleSelection.bind(this)
    this.handleColorSelection = this.handleColorSelection.bind(this)
    this.handlePlusProduct = this.handlePlusProduct.bind(this)
    this.scrollableThumbnailContainerRef = React.createRef()
    this.handleTouchStart = this.handleTouchStart.bind(this)
    this.handleTouchMove = this.handleTouchMove.bind(this)
    this.handleTouchEnd = this.handleTouchEnd.bind(this)
    this.touchStartX = React.createRef()
    this.touchLastX = React.createRef()
    this.touchLastTime = React.createRef()
  }

  componentDidMount() {
    this.checkIfScrollable()
    const {
      data: { product },
    } = this.props
    const { sku } = product.variants[0]

    const { checkUtmParamsForPromo } = this.context
    checkUtmParamsForPromo(window.location.search)

    setTimeout(() => {
      Analytics.track(`Viewed ${this.analyticsTitle()} Product Page`, { sku })
      const { size, color, style } = this.state
      Analytics.track(`Viewed ${size} ${size === '10F2' ? style : ''} ${color} Frame Product Page`)
    }, 3000)
    let url = window.location.href
    if (url.endsWith('/')) {
      url = url.slice(0, -1)
    }
    const urlParams = new URLSearchParams(new URL(url).search)
    const utmCampaignAll = urlParams.getAll('utm_campaign')
    const utmChannel = urlParams.get('channel')
    const utmAmount = decodeURIComponent(urlParams.get('amount'))

    const utmCampaign = utmCampaignAll.length > 0 ? utmCampaignAll[0] : null

    const utmTermAll = urlParams.getAll('utm_term')
    const utmTerm = utmTermAll.length > 0 ? utmTermAll[0] : null

    if (utmCampaign === 'Holiday' || utmCampaign === 'FrameHoliday') {
      const { updateBanner } = this.context
      const amount = utmCampaign === 'FrameHoliday' ? '$30' : '$20'
      updateBanner(utmTerm, utmCampaign, amount)
    }
    if (utmChannel === 'PodcastFrame') {
      const { updateBanner } = this.context
      updateBanner(utmTerm, utmChannel, utmAmount)
    }
  }

  componentDidUpdate() {
    this.checkIfScrollable()
  }

  handleSizeSelection(size) {
    this.setState({ size, color: 'Black', style: 'Classic' })
  }

  handleStyleSelection(style) {
    const styleColorMap = {
      Cover: 'Black',
      Wood: 'Walnut',
      'Shadow Box': 'Charcoal',
    }
    const color = styleColorMap[style] || 'Black'
    this.setState({ style, color })
  }

  handleColorSelection(string) {
    this.setState({ color: string })
  }

  handlePlusProduct(bool) {
    this.setState({ isPlusProduct: bool })
  }

  handleTouchStart(e) {
    this.touchStartX.current = e.touches[0].clientX
    this.touchLastX.current = e.touches[0].clientX // capture this in case the user taps and doesn't swipe
    this.touchLastTime.current = e.timeStamp
  }

  handleTouchMove(e) {
    const touchX = e.touches[0].clientX
    this.touchLastX.current = touchX // when the movement stops, but the touch isn't necessarily released
    this.touchLastTime.current = e.timeStamp
  }

  handleTouchEnd(e, images) {
    const touchEndX = e.changedTouches[0].clientX
    const { shownImageIndex } = this.state

    // Calculate swipe distance and velocity
    const swipeDistance = touchEndX - this.touchStartX.current
    const timeDelta = e.timeStamp - this.touchLastTime.current
    const swipeVelocity = timeDelta > 0 ? (touchEndX - this.touchLastX.current) / timeDelta : 0
    const minSwipeDistance = 50 // Minimum distance for a swipe to be registered
    const minSwipeVelocity = 0.3 // Minimum velocity for a swipe to be registered

    if (Math.abs(swipeDistance) > minSwipeDistance || Math.abs(swipeVelocity) > minSwipeVelocity) {
      // Swipe right - go to previous image
      if (swipeDistance > 0 || swipeVelocity > 0)
        return this.setShownImageIndex(Math.max(0, shownImageIndex - 1))

      // Swipe left - go to next image
      return this.setShownImageIndex(Math.min(images.length - 1, shownImageIndex + 1))
    }
  }

  setShownImageIndex(shownImageIndex) {
    this.setState({ shownImageIndex })
  }

  checkIfScrollable() {
    const scrollableElement = this.scrollableThumbnailContainerRef.current
    if (scrollableElement) {
      const newisScrollable = scrollableElement.scrollWidth > scrollableElement.clientWidth
      const { isScrollable } = this.state
      if (newisScrollable !== isScrollable) {
        this.setState({ isScrollable: newisScrollable })
      }
    }
  }

  scrollLeft() {
    if (this.scrollableThumbnailContainerRef.current) {
      this.scrollableThumbnailContainerRef.current.scrollBy({ left: -130, behavior: 'smooth' })
    }
  }

  scrollRight() {
    if (this.scrollableThumbnailContainerRef.current) {
      this.scrollableThumbnailContainerRef.current.scrollBy({ left: 130, behavior: 'smooth' })
    }
  }

  // eslint-disable-next-line class-methods-use-this
  analyticsTitle() {
    return 'Frame'
  }

  render() {
    const { shownImageIndex, isPlusProduct, color, size, style, isScrollable } = this.state
    const isFrame2 = size === '10F2'
    const { data, pageContext, t } = this.props
    const { product, returnGuarantee, badge, frameSEOImage } = data
    const {
      variants: [{ storefrontId: returnGuaranteeId }],
    } = returnGuarantee || { variants: [{ storefrontId: null }] }
    let { images } = product
    const {
      frame15,
      frameBlack,
      frameWhite,
      frameSilver,
      frameGold,
      frameClassicBlack,
      frameClassicWhite,
      frameClassicSage,
      frameClassicLagoon,
      frameCoverBlack,
      frameCoverWhite,
      frameWoodWalnut,
      frameWoodBirch,
      frameShadowBoxCharcoal,
      frameShadowBoxAluminum,
      frameClassicBlackWithPlus,
      frameClassicWhiteWithPlus,
      frameClassicSageWithPlus,
      frameClassicLagoonWithPlus,
      frameCoverBlackWithPlus,
      frameCoverWhiteWithPlus,
      frameWoodWalnutWithPlus,
      frameWoodBirchWithPlus,
      frameShadowBoxCharcoalWithPlus,
      frameShadowBoxAluminumWithPlus,
    } = pageContext

    let productDescription = product
    let frameWithPlus = {}
    let frameWithoutPlus = {}
    if (!IS_FOREIGN_COUNTRY) {
      const productMap = {
        '10.Black': frameBlack.data.shopifyProduct,
        '10.Silver': frameSilver.data.shopifyProduct,
        '10.White': frameWhite.data.shopifyProduct,
        '10.Gold': frameGold.data.shopifyProduct,
        '15.Black': frame15.data.shopifyProduct,
        '10F2.Black': frameClassicBlack.data.shopifyProduct,
        '10F2.White': frameClassicWhite.data.shopifyProduct,
        '10F2.Sage': frameClassicSage.data.shopifyProduct,
        '10F2.Lagoon': frameClassicLagoon.data.shopifyProduct,
        '10F2.Aluminum': frameShadowBoxAluminum.data.shopifyProduct,
        '10F2.Charcoal': frameShadowBoxCharcoal.data.shopifyProduct,
        '10F2.Walnut': frameWoodWalnut.data.shopifyProduct,
        '10F2.Birch': frameWoodBirch.data.shopifyProduct,
        '10F2.Cover.Black': frameCoverBlack.data.shopifyProduct,
        '10F2.Cover.White': frameCoverWhite.data.shopifyProduct,
        '10F2.Black.Plus': frameClassicBlackWithPlus.data.shopifyProduct,
        '10F2.White.Plus': frameClassicWhiteWithPlus.data.shopifyProduct,
        '10F2.Sage.Plus': frameClassicSageWithPlus.data.shopifyProduct,
        '10F2.Lagoon.Plus': frameClassicLagoonWithPlus.data.shopifyProduct,
        '10F2.Aluminum.Plus': frameShadowBoxAluminumWithPlus.data.shopifyProduct,
        '10F2.Charcoal.Plus': frameShadowBoxCharcoalWithPlus.data.shopifyProduct,
        '10F2.Walnut.Plus': frameWoodWalnutWithPlus.data.shopifyProduct,
        '10F2.Birch.Plus': frameWoodBirchWithPlus.data.shopifyProduct,
        '10F2.Cover.Black.Plus': frameCoverBlackWithPlus.data.shopifyProduct,
        '10F2.Cover.White.Plus': frameCoverWhiteWithPlus.data.shopifyProduct,
      }

      let imageKey = `${size}.${color}`
      if (isFrame2) {
        if (style === 'Cover') {
          imageKey = `${size}.Cover.${color}`
        }
        frameWithPlus = productMap[`${imageKey}.Plus`] || null
        frameWithoutPlus = productMap[imageKey] || null
        if (isPlusProduct) {
          images = frameWithPlus.images || images
          productDescription = frameWithPlus
        } else {
          images = frameWithoutPlus.images || images
          productDescription = frameWithoutPlus
        }
      } else {
        images = productMap[imageKey].images || images
        productDescription = productMap[`${size}.${color}`]
      }
    }
    if (size === '15') {
      images = frame15.data?.shopifyProduct?.images
      productDescription = frame15.data?.shopifyProduct
    }
    if (IS_FOREIGN_COUNTRY && size !== '15' && frameBlack) {
      images = frameBlack.data.shopifyProduct.images || images
      productDescription = frameBlack.data.shopifyProduct || productDescription
    }
    const shownImage = images[shownImageIndex]

    const is15inFrame = color === 'Black' && size !== '15'
    const badgeData = badge.edges[0].node
    const badgeList = badgeData.showPdpBadge

    const includes15inFrame = badgeList.includes('15" Black Frame')
    const hasColorBadge = badgeList.some((frame) => frame.includes(color))

    // Begin Seasonal Config
    const {
      store: {
        banner: { promoCode },
      },
    } = this.context
    const isSeasonalState = IN_SEASONAL_STATE || promoCode === VIP_CODE

    let displayBadge = isSeasonalState
      ? shownImageIndex === 0
      : (!IS_FOREIGN_COUNTRY && (is15inFrame ? includes15inFrame : hasColorBadge)) ||
        badgeList.includes(LOCALIZATION)

    if (!IS_FOREIGN_COUNTRY) {
      displayBadge = isSeasonalState ? shownImageIndex === 0 : true
    }

    const {
      Badge_50,
      Badge_45,
      Badge_40,
      Badge_32,
      Badge_30,
      Badge_70,
      Badge_100,
      Badge_50Pounds,
    } = data

    const { US, UK, CA, AU } = LOCALIZATION_MAP

    const seasonalBadgeKey = `${size}.${style}.${color}`

    const seasonalBadgeData = {
      [US]: {
        '10.Classic.Black': Badge_50.childImageSharp,
        '10.Classic.Silver': Badge_40.childImageSharp,
        '10.Classic.White': Badge_50.childImageSharp,
        '10.Classic.Gold': Badge_32.childImageSharp,
        '15.Classic.Black': Badge_45.childImageSharp,
        '10F2.Classic.Black': Badge_30.childImageSharp,
        '10F2.Classic.White': Badge_30.childImageSharp,
        '10F2.Classic.Sage': Badge_30.childImageSharp,
        '10F2.Classic.Lagoon': Badge_30.childImageSharp,
        '10F2.Shadow Box.Aluminum': Badge_30.childImageSharp,
        '10F2.Shadow Box.Charcoal': Badge_30.childImageSharp,
        '10F2.Wood.Walnut': Badge_30.childImageSharp,
        '10F2.Wood.Birch': Badge_30.childImageSharp,
        '10F2.Cover.Black': Badge_30.childImageSharp,
        '10F2.Cover.White': Badge_30.childImageSharp,
      },
      [UK]: {
        '10.Classic.Black': Badge_50Pounds.childImageSharp,
        '15.Classic.Black': Badge_50Pounds.childImageSharp,
      },
      [CA]: {
        '10.Classic.Black': Badge_50.childImageSharp,
        '15.Classic.Black': Badge_70.childImageSharp,
      },
      [AU]: {
        '10.Classic.Black': Badge_100.childImageSharp,
      },
    }

    const badgeType = IS_FOREIGN_COUNTRY ? 'internationalBadge' : 'usaFrameBadge'
    const bauBadge =
      shownImageIndex < badgeData[badgeType].length ? badgeData[badgeType][shownImageIndex] : null
    const shownBadge = isSeasonalState
      ? seasonalBadgeData[LOCALIZATION][seasonalBadgeKey]
      : bauBadge

    // if (IN_UK) {
    //   shownBadge = btsBadge.childImageSharp
    // }

    // Can use produce handle for more granular control
    const metaMap = {
      '10F2': {
        title: 'Skylight Frame 2 | Digital Picture Frame',
        description: product.description,
        imagePath: product.images[0].gatsbyImageData.images.fallback.src,
        canonical: '/products/skylight-frame-2-wood-walnut/',
      },
      fallback: {
        title: 'Skylight Frame | Digital Picture Frame',
        description:
          'The Skylight Frame is a digital touchscreen picture frame that lets you share photos with loved ones almost instantly. From anywhere in the world!',
        imagePath: frameSEOImage.childImageSharp.gatsbyImageData.images.fallback.src,
        canonical: '/products/the-skylight-frame/',
      },
    }

    const activeSEO = { ...metaMap.fallback, ...metaMap[size] }

    return (
      <>
        <SEO
          title={t(activeSEO.title)}
          description={product.description}
          imagePath={activeSEO.imagePath}
          canonicalPath={activeSEO.canonical}
          excludeTitleTagPrefix
          path={`/products/${product.handle}/`}
        />
        <Section>
          <Container>
            <Grid>
              <ProductGridLeft>
                <ProductImage
                  image={shownImage.gatsbyImageData}
                  key={shownImage.id}
                  alt={product.title}
                  className={size === '15' ? 'zoom' : 'standard'}
                  onTouchStart={this.handleTouchStart}
                  onTouchMove={this.handleTouchMove}
                  onTouchEnd={(e) => this.handleTouchEnd(e, images)}
                />
                {displayBadge && shownBadge && (
                  <GiftModeImage>
                    <GatsbyImage
                      image={shownBadge.gatsbyImageData}
                      alt="Frame Product Page Badge"
                    />
                  </GiftModeImage>
                )}
                <ArrowContainer>
                  {isScrollable && (
                    <LeftArrow
                      type="button"
                      onClick={() => this.scrollLeft()}
                      aria-label="Previous images"
                    >
                      ‹
                    </LeftArrow>
                  )}
                  <ThumbnailRow
                    ref={this.scrollableThumbnailContainerRef}
                    start={isScrollable.toString()}
                  >
                    {images.map((image, index) => (
                      <ThumbnailButton
                        key={index}
                        type="button"
                        onClick={() => {
                          this.setShownImageIndex(index)
                        }}
                      >
                        <Thumbnail
                          image={image.gatsbyImageData}
                          key={image.id}
                          alt={`${product.title} Thumbnail ${index}`}
                        />
                      </ThumbnailButton>
                    ))}
                  </ThumbnailRow>
                  {isScrollable && (
                    <RightArrow
                      type="button"
                      onClick={() => this.scrollRight()}
                      aria-label="Next images"
                    >
                      ›
                    </RightArrow>
                  )}
                </ArrowContainer>
              </ProductGridLeft>
              <ProductGridRight>
                <FrameProductForm
                  product={product}
                  frame15={frame15}
                  frameBlack={frameBlack}
                  frameWhite={frameWhite}
                  frameSilver={frameSilver}
                  frameGold={frameGold}
                  handle={product.handle}
                  title={product.title}
                  simplifiedProductTitle={this.analyticsTitle()}
                  handleSizeSelection={this.handleSizeSelection}
                  handleStyleSelection={this.handleStyleSelection}
                  updateSelectedColor={this.handleColorSelection}
                  isPlusProduct={this.handlePlusProduct}
                  setShownImageIndex={this.setShownImageIndex}
                  size={size}
                  isFrame2={isFrame2}
                  style={style}
                  color={color}
                  frameWithPlus={frameWithPlus}
                  frameWithoutPlus={frameWithoutPlus}
                  returnGuaranteeId={returnGuaranteeId}
                />
                <ShopifyDescription
                  dangerouslySetInnerHTML={{ __html: productDescription.descriptionHtml }}
                />
              </ProductGridRight>
            </Grid>
            <Anchor id="review" />
          </Container>
          <ReviewsSection productId="7073751498804" name="The Skylight Frame" />
        </Section>
      </>
    )
  }
}

FrameProductPage.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
  t: PropTypes.func,
}

export const query = graphql`
  query ($handle: String!, $language: String!) {
    product: shopifyProduct(handle: { eq: $handle }) {
      id
      title
      handle
      productType
      description
      descriptionHtml
      shopifyId
      variants {
        id
        title
        price
        shopifyId
        sku
      }
      images {
        id
        gatsbyImageData
      }
    }
    returnGuarantee: shopifyProduct(handle: { eq: "120-days-guarantee" }) {
      variants {
        storefrontId
      }
    }
    locales: allLocale(
      filter: { ns: { in: ["common", "frame-product"] }, language: { eq: $language } }
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    badge: allContentfulFrameCalendarProductPageBadgeV2 {
      edges {
        node {
          usaFrameBadge {
            gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
          }
          internationalBadge {
            gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
          }
          showPdpBadge
        }
      }
    }
    btsBadge: file(relativePath: { eq: "bts/back-to-school-sale.webp" }) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
      }
    }
    Badge_50: file(relativePath: { eq: "bfcm2025/50DollarBadgeNoFrame.png" }) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
      }
    }
    Badge_45: file(relativePath: { eq: "bfcm2025/45DollarBadge.png" }) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
      }
    }
    Badge_40: file(relativePath: { eq: "bfcm2025/40DollarBadge.png" }) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
      }
    }
    Badge_32: file(relativePath: { eq: "bfcm2025/32DollarBadge.png" }) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
      }
    }
    Badge_30: file(relativePath: { eq: "bfcm2025/30DollarBadge.png" }) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
      }
    }
    Badge_70: file(relativePath: { eq: "bfcm2025/70DollarBadge.webp" }) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
      }
    }
    Badge_100: file(relativePath: { eq: "bfcm2025/100DollarBadge.png" }) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
      }
    }
    Badge_50Pounds: file(relativePath: { eq: "bfcm2025/50PoundsBadge.png" }) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
      }
    }
    frameSEOImage: file(relativePath: { eq: "meta-seo/frame-logo.jpg" }) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
      }
    }
  }
`
FrameProductPage.contextType = StoreContext

export default withTranslation()(FrameProductPage)
